import React, { useMemo, useState, useEffect } from "react";
// import { useIntl } from "react-intl";

import { Dropdown, Form, FormField, FormGroup } from "semantic-ui-react";

// Component
import TableEmployeeLeaveRequest from "./TableEmployeeLeaveRequest";
import ModConfirmRejectRequest from "./ModConfirmRejectRequest";

// Common
import DateTextBox from "react-lib/apps/common/DateTextBox";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SearchBoxDropdown from "react-lib/appcon/common/SearchBoxDropdown";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";

// Interface
import {
  SetProp,
  RunSequence,
  PickedProps,
  EmployeeLeaveRequestType,
  DEFAULT_LIMIT,
} from "./sequence/WorkSchedule";

import { getShiftTimeValuesMap } from "./LeaveRequestListRendering";

// type
type CardEmployeeLeaveRequestProps = {
  onEvent: (e: any) => any;
  setProp: SetProp;
  // seq
  runSequence: RunSequence;
  employeeLeaveRequest?: EmployeeLeaveRequestType;
  divisionOptions: Record<string, any>[];
  masterData: any;
  // config
  readOnly?: boolean;
} & PickedProps;

export type ShiftTimeSaveType = {
  id: number;
  leave_start_time: string;
  leave_end_time: string;
  date: string;
};

export type ShiftTimeMapType = Record<number, ShiftTimeSaveType[]>;

const CardEmployeeLeaveRequest = (props: CardEmployeeLeaveRequestProps) => {
  const [showRejectModal, setShowRejectModal] = useState<boolean>(false);
  const [rejectId, setRejectId] = useState<number | null>(null);
  const [shiftTimeMap, setShiftTimeMap] = useState<ShiftTimeMapType>({});

  const leaveTypeList = useMemo(
    () => props.masterData?.leaveType || [],
    [props.masterData?.leaveType]
  );

  const leaveRequestList = useMemo(() => {
    return props.employeeLeaveRequest?.pagination?.items || [];
  }, [props.employeeLeaveRequest]);

  useEffect(() => {
    setShiftTimeMap(getShiftTimeValuesMap(leaveRequestList));
  }, [leaveRequestList]);

  const activePage = useMemo(
    () => props.employeeLeaveRequest?.pagination?.activePage || 1,
    [props.employeeLeaveRequest]
  );

  const handleChangeDate = (name: "start_date" | "end_date") => (value: string) => {
    handleChangeData(null, { name, value });
  };

  const handleChangeData = (e: any, data: any) => {
    const { checked, name, value } = data;

    const newValue = typeof checked === "boolean" ? checked : value;
    const filter = props.employeeLeaveRequest?.filter;

    props.setProp("WorkScheduleSequence.employeeLeaveRequest.filter", {
      ...filter,
      [name]: newValue,
    });
  };

  const handleChangeShiftTime = (id: number, values: ShiftTimeSaveType[]) => {
    setShiftTimeMap((prev) => {
      const others = (prev[id] || []).filter((s) => !values.some((v) => v.date === s.date));

      return {
        ...prev,
        [id]: [...others, ...values],
      };
    });
  };

  const handleOpenModReject = (id: number) => {
    setShowRejectModal(true);
    setRejectId(id);
  };

  const handleSelectedEmployee = (value: any) => {
    const filter = props.employeeLeaveRequest?.filter;

    props.setProp("WorkScheduleSequence.employeeLeaveRequest.filter", {
      ...filter,
      employee_list: value,
    });
  };

  const handleApprove = (id: number) => {
    const shiftTimeRequest = shiftTimeMap[id] || [];
    const hasValidShift = shiftTimeRequest.some((st) => st.id !== 0);

    const payload: any = {
      sequence: "WorkSchedule",
      menu: "LeaveRequest",
      action: "APPROVAL",
      status: "A",
      requestId: id,
      activePage,
    };

    if (hasValidShift) {
      payload.work_shift = shiftTimeRequest;
    }

    props.runSequence(payload);
  };

  const handleUnapprove = (id: number) => {
    props.runSequence({
      sequence: "WorkSchedule",
      menu: "LeaveRequest",
      action: "APPROVAL",
      status: "U",
      requestId: id,
      activePage,
    });
  };

  const handleConfirmReject = ({ id, remark }: { id: number; remark: string }) => {
    props.runSequence({
      sequence: "WorkSchedule",
      menu: "LeaveRequest",
      action: "APPROVAL",
      requestId: id,
      status: "X",
      remark,
      activePage,
    });

    setShowRejectModal(false);
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "WorkSchedule",
      menu: "LeaveRequest",
      action: "FETCH",
      filter: props.employeeLeaveRequest?.filter,
      activePage,
    });
  };

  const handlePageChange = (activePage: number) => {
    props.runSequence({
      sequence: "WorkSchedule",
      menu: "LeaveRequest",
      action: "FETCH",
      filter: props.employeeLeaveRequest?.filter,
      activePage,
    });
  };

  return (
    <div>
      {/* Modal Confirm */}
      <ModConfirmRejectRequest
        open={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        id={rejectId ?? 0}
        onConfirmReject={handleConfirmReject}
      />

      {/* Filter Section */}
      <Form style={{ marginLeft: "1rem" }}>
        <FormGroup inline style={{ marginBottom: "0px" }}>
          <FormField inline width={3}>
            <label>Department of Affiliation</label>
          </FormField>
        </FormGroup>
        <FormGroup inline>
          <FormField inline width={3}>
            <div style={{ width: "100%" }}>
              <Dropdown
                id="CardEmployeeLeaveRequest-Dropdown-Affiliation"
                selection
                fluid
                search
                clearable
                name="affiliate"
                options={props.divisionOptions}
                onChange={handleChangeData}
                value={props.employeeLeaveRequest?.filter?.affiliate || undefined}
              />
            </div>
          </FormField>
          <FormField inline width={3}>
            <div style={{ width: "100%" }}>
              <SearchBoxDropdown
                onEvent={props.onEvent}
                // config
                placeholder="Search Employee"
                type="Employee"
                id="CardEmployeeLeaveRequest-SearchBoxDropdown-Employee"
                fluid
                useWithKey
                icon="search"
                limit={20}
                // Select
                searchedItemListWithKey={props.searchedItemListWithKey}
                selectedItem={props.employeeLeaveRequest?.filter?.employee_list || null}
                setSelectedItem={handleSelectedEmployee}
                style={{ width: "100%" }}
              />
            </div>
          </FormField>
          <FormField inline style={{ marginLeft: "2rem" }}>
            <label>Leave Date</label>
          </FormField>
          <FormField inline width={2}>
            <DateTextBox
              id="CardEmployeeLeaveRequest-DateTextBox-StartDate"
              value={props.employeeLeaveRequest?.filter?.start_date || ""}
              onChange={handleChangeDate("start_date")}
            />
          </FormField>
          <FormField inline>
            <label>To</label>
          </FormField>
          <FormField inline width={2}>
            <DateTextBox
              id="CardEmployeeLeaveRequest-DateTextBox-EndDate"
              value={props.employeeLeaveRequest?.filter?.end_date || ""}
              onChange={handleChangeDate("end_date")}
            />
          </FormField>
          <FormField inline>
            <ButtonLoadCheck
              id="CardEmployeeLeaveRequest-ButtonLoadCheck-Search"
              // func
              setProp={props.setProp}
              onClick={handleSearch}
              // data
              paramKey={`CardWordSchedule_LeaveRequest_FETCH`}
              buttonLoadCheck={props.buttonLoadCheck?.[`CardWordSchedule_LeaveRequest_FETCH`]}
              // config
              title="Search"
              color="blue"
              name="FETCH"
              size="medium"
            />
          </FormField>
        </FormGroup>
      </Form>

      {/* Table Section */}
      <div style={{ padding: "0 0.75rem" }}>
        <TableEmployeeLeaveRequest
          // seq
          setProp={props.setProp}
          // func
          onOpenReject={handleOpenModReject}
          onApprove={handleApprove}
          onUnapprove={handleUnapprove}
          onChangeShiftTime={handleChangeShiftTime}
          // data
          leaveRequestList={leaveRequestList}
          leaveTypeList={leaveTypeList}
          shiftTimeMap={shiftTimeMap}
          // options
          buttonLoadCheck={props.buttonLoadCheck}
        />
      </div>

      {/* Pagination */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginRight: "1rem",
          marginTop: "0.75rem",
        }}
      >
        <PaginationShorthand
          activePage={props.employeeLeaveRequest?.pagination?.activePage || 0}
          limit={DEFAULT_LIMIT}
          total={props.employeeLeaveRequest?.pagination?.total}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default CardEmployeeLeaveRequest;
